import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import Icone from './Icones';
import Logo from './Logo';
import PerfilModal from './PerfilModal';
import { ID_GA4, identificarUsuarioLogado, installGoogleTag, trackPageView } from '../lib/gtm';

function distanciaMetros(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const raio = 6371000;
  const paraRad = (v: number) => (v * Math.PI) / 180;
  const dLat = paraRad(bLat - aLat);
  const dLng = paraRad(bLng - aLng);
  const lat1 = paraRad(aLat);
  const lat2 = paraRad(bLat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * raio * Math.asin(Math.sqrt(h));
}

function RastreamentoTecnicoWeb() {
  const { user } = useAuth();
  const [estado, setEstado] = useState<'idle' | 'solicitando' | 'ativo' | 'negado' | 'erro'>('idle');
  const [detalhe, setDetalhe] = useState('');
  const [tentativa, setTentativa] = useState(0);
  const watchRef = useRef<number | null>(null);
  const intervaloRef = useRef<number | null>(null);
  const ultimoEnvioRef = useRef(0);
  const ultimaPosicaoRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const deveRastrear = Boolean(
    user?.perfil === 'tecnico'
    && user?.rastreamento_tecnico_habilitado
    && user?.pode_ver_localizacao_tecnico,
  );

  useEffect(() => {
    if (!deveRastrear) {
      setEstado('idle');
      setDetalhe('');
      if (watchRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      if (intervaloRef.current !== null) {
        window.clearInterval(intervaloRef.current);
        intervaloRef.current = null;
      }
      return;
    }

    if (!navigator.geolocation) {
      setEstado('erro');
      setDetalhe('Este navegador nao suporta localizacao em tempo real.');
      return;
    }

    let ativo = true;
    const opcoes: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 15000,
    };

    const enviarPosicao = async (pos: GeolocationPosition) => {
      if (!ativo) return;
      const latitude = Number(pos.coords.latitude);
      const longitude = Number(pos.coords.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      const agora = Date.now();
      const anterior = ultimaPosicaoRef.current;
      const distancia = anterior ? distanciaMetros(anterior.latitude, anterior.longitude, latitude, longitude) : Number.POSITIVE_INFINITY;
      if (anterior && agora - ultimoEnvioRef.current < 45000 && distancia < 25) {
        setEstado('ativo');
        setDetalhe('');
        return;
      }

      ultimoEnvioRef.current = agora;
      ultimaPosicaoRef.current = { latitude, longitude };
      setEstado('ativo');
      setDetalhe('');

      try {
        await api('/mobile/localizacao_tecnico.php', {
          method: 'POST',
          body: {
            latitude,
            longitude,
            precisao_metros: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
            origem: 'web',
          },
        });
      } catch {
        if (!ativo) return;
        setEstado('erro');
        setDetalhe('Nao foi possivel salvar sua localizacao agora. Vamos tentar novamente.');
      }
    };

    const tratarErro = (erroGeo: GeolocationPositionError) => {
      if (!ativo) return;
      if (erroGeo.code === 1) {
        setEstado('negado');
        setDetalhe('A localizacao deste tecnico esta configurada como obrigatoria. Libere o acesso no navegador para continuar.');
        return;
      }
      setEstado('erro');
      setDetalhe('Nao foi possivel obter sua localizacao neste momento.');
    };

    const solicitar = () => {
      if (!ativo) return;
      setEstado((atual) => (atual === 'ativo' ? atual : 'solicitando'));
      navigator.geolocation.getCurrentPosition(enviarPosicao, tratarErro, opcoes);
    };

    solicitar();
    watchRef.current = navigator.geolocation.watchPosition(enviarPosicao, tratarErro, opcoes);

    const aoVoltar = () => {
      if (document.visibilityState === 'visible') solicitar();
    };

    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    intervaloRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') solicitar();
    }, 120000);

    return () => {
      ativo = false;
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
      if (watchRef.current !== null) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      if (intervaloRef.current !== null) {
        window.clearInterval(intervaloRef.current);
        intervaloRef.current = null;
      }
    };
  }, [deveRastrear, tentativa]);

  if (!deveRastrear || estado === 'ativo' || estado === 'idle') return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        background: 'rgba(15, 23, 42, 0.58)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: '#fff',
          borderRadius: 22,
          padding: 24,
          boxShadow: '0 30px 80px rgba(15, 23, 42, .28)',
          border: '1px solid #dbe7ff',
        }}
      >
        <strong style={{ display: 'block', fontSize: 20, color: '#0a49c2' }}>
          {estado === 'negado' ? 'Libere sua localizacao' : 'Ativando rastreabilidade'}
        </strong>
        <p style={{ margin: '10px 0 0', color: 'var(--texto-suave)', lineHeight: 1.55 }}>
          {detalhe || 'Precisamos da sua localizacao para registrar latitude e longitude deste tecnico na versao web.'}
        </p>
        <div className="form-actions" style={{ marginTop: 18 }}>
          <button type="button" className="btn" onClick={() => setTentativa((atual) => atual + 1)}>
            {estado === 'solicitando' ? 'Solicitando localizacao...' : 'Tentar novamente'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const logoEmpresa22 = `${process.env.PUBLIC_URL || '.'}/company-logos/cnservicos-22.png`;
  const { user, empresas, selecionarEmpresa, logout, isPremium, isConsumoDisponivel, isFinanceiroDisponivel, isIntegracaoPagamentosDisponivel, isServicosDisponivel, isOsSimplificadaDisponivel, isSemPmoc } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuAberto, setMenuAberto] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [perfilAberto, setPerfilAberto] = useState(false);
  const [sidebarColapsada, setSidebarColapsada] = useState(() => localStorage.getItem('lizzi_sidebar_colapsada') === '1');
  const [logoEmpresa, setLogoEmpresa] = useState<string>('');
  const [nomeMarcaEmpresa, setNomeMarcaEmpresa] = useState('');

  // Google Analytics 4 do app logado. A landing tem o seu (Landing.tsx); aqui a
  // mesma propriedade passa a medir o uso de quem ja e cliente. O page_view
  // automatico fica desligado porque a SPA troca de rota sem recarregar - quem
  // manda page_view e o efeito de location abaixo, senao a 1a tela contaria duas
  // vezes e as seguintes, nenhuma.
  useEffect(() => {
    installGoogleTag(ID_GA4, { enviarPageView: false });
  }, []);

  // Identificacao pseudonima (IDs e rotulos, nunca nome/e-mail) para segmentar uso
  // por plano, perfil e modo. Reage a troca de empresa, que muda tenant e plano.
  useEffect(() => {
    if (!user) return;
    identificarUsuarioLogado({
      usuarioId: user.id,
      empresaId: user.empresa_id,
      perfil: user.perfil,
      plano: user.plano,
      modoVisualizacao: user.modo_visualizacao,
    });
  }, [user]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let ativo = true;
    if (user?.empresa_id !== 22) {
      setLogoEmpresa('');
      setNomeMarcaEmpresa('');
      return () => { ativo = false; };
    }
    api<{ logo_url?: string | null; nome_fantasia?: string | null; razao_social?: string | null }>('/config_empresa/configuracoes.php')
      .then((cfg) => {
        if (!ativo) return;
        setLogoEmpresa(logoEmpresa22);
        setNomeMarcaEmpresa(cfg.nome_fantasia || cfg.razao_social || user?.empresa_nome || 'Empresa');
      })
      .catch(() => {
        if (!ativo) return;
        setLogoEmpresa(logoEmpresa22);
        setNomeMarcaEmpresa(user?.empresa_nome || 'Empresa');
      });
    return () => { ativo = false; };
  }, [logoEmpresa22, user?.empresa_id, user?.empresa_nome]);

  function alternarSidebar() {
    setSidebarColapsada((atual) => {
      const novo = !atual;
      localStorage.setItem('lizzi_sidebar_colapsada', novo ? '1' : '0');
      return novo;
    });
  }

  async function sair() { await logout(); nav('/login'); }

  // Troca a empresa ativa da sessão e recarrega para atualizar todos os dados do tenant.
  async function trocarEmpresa(empresaId: number) {
    if (!empresaId || empresaId === user?.empresa_id) return;
    setTrocando(true);
    try {
      await selecionarEmpresa(empresaId);
      window.location.reload();
    } catch {
      setTrocando(false);
    }
  }

  const cls = ({ isActive }: { isActive: boolean }) => (isActive ? 'ativo' : '');
  const fechar = () => setMenuAberto(false);
  const podeGerenciarEmpresa = ['admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeGerenciarUsuarios = Boolean(user?.perfil && user.perfil !== 'tecnico');
  const adminGeralGlobal = user?.perfil === 'admin_geral' && !user.empresa_id;
  const adminGeralEmEmpresa = user?.perfil === 'admin_geral' && Boolean(user.empresa_id);
  const podeMenuEmpresa = podeGerenciarEmpresa || adminGeralEmEmpresa;
  // Permissao de usuario "TECNICO PODE CADASTRAR MATERIAL": libera o menu
  // Estoque tambem para o tecnico, mesmo sem podeMenuEmpresa.
  const tecnicoPodeCadastrarMaterial = Boolean(user?.perfil === 'tecnico' && user?.pode_cadastrar_material);
  const modoOficina = user?.modo_visualizacao === 'oficina' && Boolean(user.empresa_id);
  const suporte = user?.suporte_acesso;
  const podeRastrearTecnicos = Boolean(
    user?.rastreamento_tecnico_habilitado
    && !(user?.perfil === 'tecnico' && user?.tecnico_sem_acesso_rastreamento_habilitado)
    && (user?.perfil === 'admin_geral' || user?.pode_ver_localizacao_tecnico),
  );
  const menuRegistroConsumoAtivo = location.pathname.startsWith('/app/registro-consumo')
    || /^\/app\/consumo\/[^/]+$/.test(location.pathname);
  const menuMedidoresAtivo = location.pathname === '/app/consumo';

  return (
    <div className="app">
      <aside className={'sidebar' + (menuAberto ? ' aberto' : '') + (sidebarColapsada ? ' colapsada' : '')} onClick={fechar}>
        <div className="logo">
          {user?.empresa_id === 22 && logoEmpresa ? (
            <img className="sidebar-company-logo" src={logoEmpresa} alt={nomeMarcaEmpresa || 'Logo da empresa'} />
          ) : (
            <Logo variant="light" size={30} />
          )}
        </div>
        {adminGeralGlobal ? (
          <>
            <NavLink to="/app/empresas" className={cls}><Icone nome="predio" size={18} /> Empresas</NavLink>
            <NavLink to="/app/usuarios" className={cls}><Icone nome="usuario" size={18} /> Usuários e permissões</NavLink>
          </>
        ) : modoOficina ? (
          <>
            <NavLink to="/app" end className={cls}><Icone nome="grafico" size={18} /> Dashboard</NavLink>
            <NavLink to="/app/orcamentos" className={cls}><Icone nome="prancheta" size={18} /> Criar chamado</NavLink>
            <NavLink to="/app/os" className={cls}><Icone nome="chave" size={18} /> Ordens de serviço</NavLink>
            <NavLink to="/app/agenda" className={cls}><Icone nome="calendario" size={18} /> Agenda do técnico</NavLink>
            <NavLink to="/app/clientes" className={cls}><Icone nome="usuario" size={18} /> Clientes</NavLink>
            <NavLink to="/app/materiais" className={cls}><Icone nome="caixa" size={18} /> Materiais</NavLink>
            {isServicosDisponivel && (
              <NavLink to="/app/servicos" className={cls}><Icone nome="prancheta" size={18} /> Serviços</NavLink>
            )}
            <NavLink to="/app/veiculos" className={cls}><Icone nome="chave" size={18} /> Veículos</NavLink>
            {podeGerenciarUsuarios && (
              <NavLink to="/app/usuarios" className={cls}><Icone nome="usuario" size={18} /> Usuários e permissões</NavLink>
            )}
            {podeMenuEmpresa && (
              <NavLink to="/app/relatorios" className={cls}><Icone nome="grafico" size={18} /> Relatórios</NavLink>
            )}
            {isFinanceiroDisponivel && (
              <NavLink to="/app/financeiro" className={cls}><Icone nome="grafico" size={18} /> Financeiro</NavLink>
            )}
            {isIntegracaoPagamentosDisponivel && (
              <NavLink to="/app/integracao" className={cls}><Icone nome="raio" size={18} /> Integração</NavLink>
            )}
            <NavLink to="/app/config" className={cls}><Icone nome="engrenagem" size={18} /> Configuração</NavLink>
          </>
        ) : (
          <>
            {adminGeralEmEmpresa && (
              <NavLink to="/app/empresas" className={cls}><Icone nome="predio" size={18} /> Empresas</NavLink>
            )}
            <NavLink to="/app" end className={cls}><Icone nome="grafico" size={18} /> Dashboard</NavLink>
            <NavLink to="/app/os" className={cls}><Icone nome="chave" size={18} /> Ordens de Serviço</NavLink>
            <NavLink to="/app/agenda" className={cls}><Icone nome="calendario" size={18} /> Agenda</NavLink>
            {podeRastrearTecnicos && (
              <NavLink to="/app/rastreamento" className={cls}><Icone nome="mapa" size={18} /> Rastreamento</NavLink>
            )}
            {podeGerenciarUsuarios && (
              <NavLink to="/app/usuarios" className={cls}><Icone nome="usuario" size={18} /> Usuários e permissões</NavLink>
            )}
            {podeMenuEmpresa && (
              <>
                <NavLink to="/app/relatorios" className={cls}><Icone nome="grafico" size={18} /> Relatórios</NavLink>
                <NavLink to="/app/cadastros" className={cls}><Icone nome="predio" size={18} /> Cadastros</NavLink>
                {isPremium && (
                  <>
                    {/* "CRIACAO DE OS SIMPLIFICADA": Equipamentos vira aba dentro de Cadastros,
                        e Checklist/PMOC saem do menu (o fluxo simplificado nao usa esses modulos). */}
                    {!isOsSimplificadaDisponivel && (
                      <NavLink to="/app/ativos" className={cls}><Icone nome="qr" size={18} /> Equipamentos</NavLink>
                    )}
                    {isConsumoDisponivel && (
                      <>
                        <NavLink to="/app/registro-consumo" className={() => (menuRegistroConsumoAtivo ? 'ativo' : '')}><Icone nome="grafico" size={18} /> Registro de consumo</NavLink>
                        <NavLink to="/app/consumo" className={() => (menuMedidoresAtivo ? 'ativo' : '')}><Icone nome="engrenagem" size={18} /> Medidores</NavLink>
                      </>
                    )}
                    {!isOsSimplificadaDisponivel && (
                      <>
                        <NavLink to="/app/checklist" className={cls}><Icone nome="checklist" size={18} /> Checklist</NavLink>
                        {/* "RETIRAR TELA PMOC": o menu sai; a rota e a API tambem barram. */}
                        {!isSemPmoc && <NavLink to="/app/pmoc" className={cls}><Icone nome="pmoc" size={18} /> PMOC</NavLink>}
                      </>
                    )}
                  </>
                )}
                {/* Menu "Serviços": só aparece para empresa com a permissão SERVIÇOS. */}
                {isServicosDisponivel && (
                  <NavLink to="/app/servicos" className={cls}><Icone nome="prancheta" size={18} /> Serviços</NavLink>
                )}
                {isFinanceiroDisponivel && (
                  <NavLink to="/app/financeiro" className={cls}><Icone nome="grafico" size={18} /> Financeiro</NavLink>
                )}
                {isIntegracaoPagamentosDisponivel && (
                  <NavLink to="/app/integracao" className={cls}><Icone nome="raio" size={18} /> Integração</NavLink>
                )}
                <NavLink to="/app/config" className={cls}><Icone nome="engrenagem" size={18} /> Configuração</NavLink>
              </>
            )}
            {/* Permissao de usuario "TECNICO PODE CADASTRAR MATERIAL": mesmo sem
                podeMenuEmpresa, o tecnico com essa permissao ve o menu Estoque. */}
            {isPremium && (podeMenuEmpresa || tecnicoPodeCadastrarMaterial) && (
              <NavLink to="/app/estoque" className={cls}><Icone nome="caixa" size={18} /> Estoque</NavLink>
            )}
          </>
        )}
      </aside>
      <div className={'overlay' + (menuAberto ? ' ativo' : '')} onClick={fechar} />

      <div className="conteudo">
        <header className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="menu-btn" onClick={() => setMenuAberto(true)} aria-label="Abrir menu">☰</button>
            <button
              className="sidebar-toggle-btn"
              onClick={alternarSidebar}
              title={sidebarColapsada ? 'Mostrar menu lateral' : 'Ocultar menu lateral (mais espaço para preencher)'}
              aria-label={sidebarColapsada ? 'Mostrar menu lateral' : 'Ocultar menu lateral'}
            >
              {sidebarColapsada ? '»' : '«'}
            </button>
            <strong>{isPremium ? 'Plano Premium' : 'Plano Simples'}</strong>
          </div>
          <div className="user-chip">
            {empresas.length > 1 && (
              <select
                value={user?.empresa_id ?? ''}
                onChange={(e) => trocarEmpresa(Number(e.target.value))}
                disabled={trocando}
                title="Trocar empresa"
                style={{ maxWidth: 200, padding: '7px 10px' }}
              >
                {empresas.map((emp) => <option key={emp.empresa_id} value={emp.empresa_id}>{emp.empresa_nome}</option>)}
              </select>
            )}
            <span className="nome-user">{user?.nome}</span>
            {suporte && <span className="badge" title={suporte.email}>Suporte: {suporte.nome}</span>}
            <span className="badge">{user?.perfil}</span>
            <button className="btn sec sm icon-only" onClick={() => setPerfilAberto(true)} title="Meus dados" aria-label="Meus dados">
              <Icone nome="usuario" size={17} />
            </button>
            <button className="btn sec sm" onClick={sair}>Sair</button>
          </div>
        </header>
        <main className="main">
          <Outlet />
        </main>
      </div>
      {perfilAberto && <PerfilModal onClose={() => setPerfilAberto(false)} />}
      <RastreamentoTecnicoWeb />
    </div>
  );
}
